import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useHttpClient } from '@bonusly/http-client';

import { BizyDreamingPreferencesSchema } from '../schemas/bizy-dreaming-preferences';
import { headers } from './meta';

// ─── Query Keys ──────────────────────────────────────────────────────────────

enum QueryKey {
	DreamingPreferences = 'bizy-dreaming-preferences',
}

export const bizyDreamingPreferencesQueryKey = () => [QueryKey.DreamingPreferences];

// ─── Path helpers ────────────────────────────────────────────────────────────

export const bizyDreamingPreferencesPath = (prefix = '') => `${prefix}/bizy/dreaming_preferences`;

// ─── Query hooks ─────────────────────────────────────────────────────────────

/**
 * Fetches the current user's per-connector dreaming preferences.
 *
 * GET /api/v2/bizy/dreaming_preferences
 */
export const useBizyDreamingPreferences = (enabled = true) => {
	const http = useHttpClient();

	return useQuery({
		queryKey: bizyDreamingPreferencesQueryKey(),
		queryFn: async () => {
			const res = await http.get(bizyDreamingPreferencesPath(), { headers });
			return BizyDreamingPreferencesSchema.parse(res.data);
		},
		enabled,
	});
};

// ─── Mutation hooks ──────────────────────────────────────────────────────────

/**
 * Persists a connector dreaming opt-out for the current user. The request body
 * is snake_case (only responses are camelized); the response mirrors the GET
 * payload, so we write it straight back into the cache.
 *
 * PATCH /api/v2/bizy/dreaming_preferences
 */
export const useUpdateBizyDreamingPreferenceMutation = () => {
	const http = useHttpClient();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ connector, enabled }: { connector: string; enabled: boolean }) => {
			const res = await http.patch(bizyDreamingPreferencesPath(), { connector, enabled }, { headers });
			return BizyDreamingPreferencesSchema.parse(res.data);
		},
		onSuccess: (data) => {
			queryClient.setQueryData(bizyDreamingPreferencesQueryKey(), data);
		},
	});
};
